<template>
    <div class="global-dealer-search row">
        <div class="global-dealer-search__header col-12 col-lg-8">
            <slot name="header"/>
        </div>
        <div class="global-dealer-search__body col-12">
            <div class="row">
                <global-tabs @tabchanged="tabChange" header-type="headline-3" class="col-12 col-lg-8">
                    <global-tab :header="$t('global-dealer-search.searchByRadius')">
                        <div class="row">
                            <div class="form-group col-12 col-md-4 col-lg-5" ref="filterCountry" v-show="countries.length > 1">
                                <label class="form__label">{{ $t('global-dealer-search.country') }}</label>
                                <global-dropdown theme="secondary" :options="countries" @change="changeCountry($event.key)" :initial-title="getCountryName"/>
                                <p class="form__error">{{ $t('global-dealer-search.error.country') }}</p>
                            </div>
                            <div class="form-group col-12 col-md-5 col-lg-4 col-xl-5" ref="filterCity">
                                <label class="form__label">{{ $t('global-dealer-search.zipCity') }}</label>
                                <input type="text" :value="$store.getters.getCity || ''" @input="changeCity($event.target.value)"
                                       @blur="showError($refs.filterCity, $event.target.value === '')" class="form__input form__input--text"
                                       v-on:keyup.enter="validateFields"/>
                                <p class="form__error">{{ $t('global-dealer-search.error.zipCity') }}</p>
                            </div>
                            <div class="form-group"
                                 :class="{'col-4 col-md-3 col-lg-3 col-xl-2': enableTypeFilter, 'col-12 col-md-3 col-lg-3 col-xl-2': !enableTypeFilter}"
                                 ref="filterRadius">
                                <label class="form__label">{{ $t('global-dealer-search.radius') }}</label>
                                <global-dropdown theme="secondary" :options="getRadius()" @change="changeRadius($event)"
                                                 :initial-title="$store.getters.getRadius.title"
                                                 :selected-option="$store.getters.getRadius"/>
                                <p class="form__error">{{ $t('global-dealer-search.error.radius') }}</p>
                            </div>
                            <dealer-search-type-filter/>
                            <div class="button-wrapper bottom full-width" :class="{'col-12 col-md-2': enableTypeFilter, 'col-12': !enableTypeFilter}">
                                <global-button class="primary" @click="validateFields">{{ $t('global-dealer-search.button.search') }}</global-button>
                            </div>
                        </div>
                    </global-tab>
                    <global-tab :header="$t('global-dealer-search.searchByKeyword')">
                        <div class="row">
                            <div class="form-group col-12">
                                <global-keyword-search @updateDealers="handleKeywordSearch($event)" :dealers="$store.getters.dealers"/>
                            </div>
                        </div>
                    </global-tab>
                </global-tabs>
                <div class="col-12 col-lg-8">
                    <div class="devider"></div>
                </div>
                <global-map v-if="$store.getters.mapCenter" :origin="$store.getters.mapCenter" :zoom="zoomLevel" :marker="dealerResultsForMap"
                            :userLocation="$store.getters.userLocation"
                            :class="{'col-12 col-sm-6 col-lg-8': dealerResults, 'col-12 col-sm-12 col-lg-8': !dealerResults}"/>
                <div class="result-list col-12 col-sm-6 col-lg-4">
                    <div v-if="dealerResults" class="result-list__label">{{ dealerResults.length }} {{ $t('global-dealer-search.dealercount') }}</div>
                    <template v-for="(dealer, index) in dealerResults">
                        <global-info-dealer :enabled-type-filters="$store.state.availableFilters"
                                            v-show="index < initialDealersShown"
                                            :dealer-prop="dealer"
                                            :index="index+1"
                                            :enable-dealer-selection="enableDealerSelection"
                                            @choose="dealerChosen($event)"/>
                    </template>
                    <div class="button-wrapper right spacer--top" v-if="dealerResults && dealerResults.length">
                        <global-button theme="secondary" v-if="initialDealersShown < dealerResults.length" @click="initialDealersShown += 6">
                            {{ $t('global-dealer-search.loadMore') }}
                        </global-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import GlobalTabs from "@global/global-elements/global-tabs/global-tabs.vue";
import GlobalTab from "@global/global-elements/global-tabs/global-tab";
import GlobalButton from "@global/global-elements/global-button/global-button";
import GlobalDropdown from "@global/global-elements/global-dropdown/global-dropdown";
import GlobalMap from "@global/global-elements/global-map/global-map";
import {countryNameForIso2, getUserCountry} from "@global/utilities/global-country-data";
import {getInitialRadius, radi} from "@global/global-elements/global-dealer-search/utilities.js";
import GlobalInfoDealer from "@global/global-elements/global-info-item/global-info-dealer";
import GlobalKeywordSearch from "@global/global-elements/global-dealer-search/components/dealer-keyword-search.vue";
import DealerSearchTypeFilter from "@global/global-elements/global-dealer-search/components/dealer-search-type-filter.vue";

export default {
    name: "global-dealer-search",
    components: {
        DealerSearchTypeFilter,
        GlobalKeywordSearch,
        GlobalInfoDealer,
        GlobalMap,
        GlobalButton,
        GlobalTab,
        GlobalTabs,
        GlobalDropdown
    },
    props: {
        /** Is a dealer selectable? Dispatches 'selectDealer' into vuex store */
        enableDealerSelection: {
            type: Boolean,
            default: false
        },
        /** Only shows dealers with email set */
        requireEmail: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            countries: [],
            initialDealersShown: 6,
        }
    },
    computed: {
        enableTypeFilter() {
            return this.$store.getters.getAvailableFilters.length > 1
        },
        getCountryName() {
            return countryNameForIso2(this.$store.getters.getCountry)
        },
        zoomLevel() {
            return this.$store.getters.getZoom;
        },
        dealerResults() {
            return this.$store.getters.getResultDealers
        },
        dealerResultsForMap() {
            return Array.isArray(this.dealerResults) ? this.dealerResults.slice(0, this.initialDealersShown) : null;
        }
    },
    methods: {
        getRadius() {
            return radi
        },
        changeCountry(countryCode) {
            this.$store.dispatch("setCountry", countryCode)
        },
        changeCity(city) {
            this.$store.dispatch("setCity", city)
        },
        changeRadius(radius) {
            this.$store.dispatch("setRadius", radius)
        },
        showError(ref, show) {
            if (show) {
                ref.classList.add('has-error')
            } else {
                ref.classList.remove('has-error')
            }
            return show
        },
        validateFields() {
            this.initialDealersShown = 6;
            let anyErrors = false;
            anyErrors = anyErrors || this.showError(this.$refs.filterCity, this.$store.getters.getCity === null);
            anyErrors = anyErrors || this.showError(this.$refs.filterCountry, this.$store.getters.getCountry === null);
            anyErrors = anyErrors || this.showError(this.$refs.filterRadius, this.$store.getters.getRadius.key === null);

            if (anyErrors) {
                return false
            }

            this.$store.dispatch('findDealersByFilter')
        },
        handleKeywordSearch(dealers) {
            this.$store.dispatch('setDealerResults', dealers)
        },
        updateCountries() {
            const dealers = this.$store.getters.dealers

            const countryMap = dealers.reduce((map, dealer) => {
                const key = dealer.country.trim().toUpperCase()
                if (!map.has(key)) {
                    map.set(key, { key, title: countryNameForIso2(key) })
                }
                return map
            }, new Map())

            this.countries = Array.from(countryMap.values()).sort((a, b) => a.title.localeCompare(b.title));

            // Update Default selection i.e. if it's not in the country list
            if (!countryMap.has(this.$store.getters.getCountry)) {
                //Land fehlt in Daten
                if (getUserCountry() !== this.$store.getters.getCountry && countryMap.has(getUserCountry())) {
                    this.$store.dispatch('setCountry', getUserCountry())
                } else {
                    this.$store.dispatch('setCountry', this.countries[0].key)
                }
            }
        },
        dealerChosen(dealer) {
            this.$store.dispatch('selectDealer', dealer);
        },
        tabChange() {
            this.$store.dispatch("requireEmail", this.requireEmail)
            this.$store.dispatch("calculateAvailableFilters")
            this.updateCountries();
            this.$store.dispatch('setUserLocation', null)
            this.$store.dispatch('setRadius', getInitialRadius())
        }
    },
    mounted() {
        this.$store.dispatch("requireEmail", this.requireEmail)
        this.$store.dispatch("calculateAvailableFilters")
        this.$nextTick(() => {
            this.updateCountries();
        });
    }
}
</script>

